import { createContext, PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { VatExemptionMentionModalFormSchema } from '../types/vatExemptionMentionModalFormSchema'
import { getVatExemptionMentionModalFormSchema } from '../utils/getVatExemptionMentionModalFormSchema'

interface ContextState {
  submitForm: () => void
  vatExemptionMentionModalForm: UseFormReturn<VatExemptionMentionModalFormSchema>
}

const VatExemptionMentionModalFormContext = createContext<ContextState | undefined>(undefined)

interface VatExemptionMentionModalFormContextProviderProps extends PropsWithChildren {
  defaultValue: string
  onSubmit: (value: string) => void
}

export const VatExemptionMentionModalFormContextProvider = ({
  children,
  defaultValue,
  onSubmit,
}: VatExemptionMentionModalFormContextProviderProps): ReactElement => {
  const vatExemptionMentionModalForm = useForm<VatExemptionMentionModalFormSchema>({
    defaultValues: { vatExemptionScheme: defaultValue },
    validationSchema: getVatExemptionMentionModalFormSchema(),
  })

  const {
    Form,
    handleSubmit: handleVatExemptionMentionModalFormSubmit,
    reset: resetForm,
  } = vatExemptionMentionModalForm

  useEffect(() => {
    resetForm({ vatExemptionScheme: defaultValue })
  }, [defaultValue, resetForm])

  const submitForm = useCallback(() => {
    handleVatExemptionMentionModalFormSubmit(({ vatExemptionScheme }) => {
      const normalizedValue = vatExemptionScheme.trim()
      onSubmit(normalizedValue)
    })()
  }, [handleVatExemptionMentionModalFormSubmit, onSubmit])

  const contextValue = useMemo(
    () => ({
      submitForm,
      vatExemptionMentionModalForm,
    }),
    [submitForm, vatExemptionMentionModalForm],
  )

  return (
    <VatExemptionMentionModalFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
    </VatExemptionMentionModalFormContext.Provider>
  )
}

export const useVatExemptionMentionModalForm = () => {
  const context = useContext(VatExemptionMentionModalFormContext)

  if (!context) {
    throw new Error('VatExemptionMentionModalFormContextProvider is missing in the module!')
  }

  return context
}
